/* Client Side Only */

import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { getCookie } from 'cookies-next';

import { i18n } from '@/next-i18next.config';

class ApiClient {
  private static _instance: ApiClient;
  private _client: AxiosInstance;
  private _refreshTokenPromise: Promise<AxiosResponse | AxiosError> | null =
    null;

  private constructor() {
    this._client = axios.create({ baseURL: '/' });
    this._client.interceptors.response.use(
      (r) => r,
      (e: AxiosError<{ message?: string }>) => this._handleError(e)
    );
  }

  get client(): AxiosInstance {
    return this._client;
  }

  static get instance(): ApiClient {
    if (!ApiClient._instance) {
      ApiClient._instance = new ApiClient();
    }

    return ApiClient._instance;
  }

  private _createRefreshRequest(): Promise<AxiosResponse | AxiosError> {
    return axios
      .get('/api/auth/refresh')
      .then((response) => {
        this._refreshTokenPromise = null;

        return response;
      })
      .catch(async (error: AxiosError) => {
        this._refreshTokenPromise = null;

        if (error?.response?.status === 503) {
          const language =
            (await getCookie('NEXT_LOCALE'))?.toString() || i18n.defaultLocale;

          window.location.href = `/${language}/503`;
        } else {
          window.location.href = '/api/auth/sign-out';
        }

        return error;
      });
  }

  private async _handleError(error: AxiosError<{ message?: string }>) {
    if (error.config && error.response) {
      if (error.response.status === 401) {
        if (!this._refreshTokenPromise) {
          this._refreshTokenPromise = this._createRefreshRequest();
        }
        return this._refreshTokenPromise.then(() =>
          axios.request(error.config!)
        );
      }

      if (error.response.status === 403) {
        const language =
          (await getCookie('NEXT_LOCALE'))?.toString() || i18n.defaultLocale;

        if (error.response.data?.message === 'user.consentRequired') {
          window.location.href = `/${language}/policies`;
        } else if (
          error.response.data?.message ===
          'user.practitionerIsNotConnectedWithPatient'
        ) {
          window.location.href = `/${language}/patients/my-patients`;
        }
      }
    }

    return Promise.reject(error);
  }
}

export default ApiClient;
